import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { 
    IonGrid, IonRow, IonCol
} from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { environment } from '@app/environment';
import { MarvColorPrimary } from '@marv/types';
import { 
    MarvInputComponent, MarvButtonComponent, MarvToggleComponent, MarvInputDateComponent,
    MarvMonthSelectComponent, MarvWeekSelectComponent, MarvWeekDaySelectComponent, 
    MarvSelectItem, MarvSelectExternalSource 
} from '@marv/components';
import { MarvDateService, MarvJsonService } from '@marv/services';
import { 
    RetailerSelectComponent, AreaSelectComponent, SectorSelectComponent, TypeMissionSelectComponent,
    OperationSelectComponent, StoreSelectComponent, PermanentAgentSelectComponent
} from '@app/components';
import { OperationService, CustomerSectorService, RetailerService, StoreService } from '@app/services';
import { OperationCard } from '@app/models';

export class PlanningFiltersValues {  
    viewSelected: 'list'|'month'|'week' = 'list';  
    startDate: Date = MarvDateService.GetFirstDayOfMonth();
    endDate: Date = MarvDateService.GetLastDayOfMonth();
    month: Date = MarvDateService.GetFirstDayOfMonth();
    weekDays: Array<number>;
    week: Date = MarvDateService.GetFirstDayOfWeek();
    typesMissionsCodes: Array<string>;
    operationsIds: Array<string>;
    areasIds: Array<number>;
    sectorsIds: Array<number>;
    agentsIds: Array<number>;
    retailersCodes: Array<string>;
    retailersItems: Array<MarvSelectItem> = [];
    storesCodes: Array<string>;
    storesItems: Array<MarvSelectItem> = [];
    city: string;
    displayCancelledMissions: boolean = false;
    displayOnlyStoreMissions: boolean = false;
    displayOnlyUnratedMissions: boolean = false;
    nbFiltersFilled: number = 0;
    
    constructor(fromStorage: boolean = false) {
        let planningFiltersValues:any = this;
        if(fromStorage){
            planningFiltersValues = this.loadFilters();
        }
        return planningFiltersValues;
    }
    
    loadFilters(): PlanningFiltersValues{     
        let planningFiltersValues: any = new PlanningFiltersValues();
        let temp = sessionStorage.getItem("planning_filters_values");
        if(temp){
            let filtersValues = MarvJsonService.Parse(temp);
            Object.getOwnPropertyNames(filtersValues).forEach(k => {                
                if(typeof filtersValues[k] !== 'undefined'){
                    if(planningFiltersValues[k] instanceof Date){
                        planningFiltersValues[k] = new Date(filtersValues[k]);
                    }
                    else{
                        planningFiltersValues[k] = filtersValues[k];
                    }
                }
            });
        }        
        return planningFiltersValues;
    }
        
    saveFilters(remove: boolean = false){    
        if(remove){   
            PlanningFiltersValues.DeleteFiltersSaved(); 
        }
        else{
            sessionStorage.setItem("planning_filters_values", MarvJsonService.Stringify(this));
        }
    }
        
    static DeleteFiltersSaved(){
        sessionStorage.removeItem("planning_filters_values");
    }
        
    static FromForm(form: FormGroup, viewSelected: 'list'|'month'|'week', retailersItems: Array<MarvSelectItem>, storesItems: Array<MarvSelectItem>): PlanningFiltersValues{
        let filters = new PlanningFiltersValues();  
           
        filters.viewSelected = viewSelected;
           
        if(viewSelected === 'month'){
            filters.month = form.value.month;
            filters.startDate = MarvDateService.GetFirstDayOfMonth(form.value.month);
            filters.endDate = MarvDateService.GetLastDayOfMonth(form.value.month);
        }
        else if(viewSelected === 'week'){
            filters.week = form.value.week;
            filters.startDate = MarvDateService.GetFirstDayOfWeek(form.value.week);
            filters.endDate = MarvDateService.GetLastDayOfWeek(form.value.week);            
        }
        else{
            if(form.value?.startDate){
                filters.startDate = form.value.startDate;
            }
            if(form.value?.endDate){
                filters.endDate = form.value.endDate;
            }
        }
                
        if(form.value?.weekDays && form.value.weekDays.length > 0){
            filters.weekDays = form.value.weekDays;
            if(filters.weekDays.length != 7){
                filters.nbFiltersFilled++;
            }
        }
        
        if(form.value?.typesMissionsCodes && form.value.typesMissionsCodes.length > 0){
            filters.typesMissionsCodes = form.value.typesMissionsCodes;
            filters.nbFiltersFilled++;
        }
        if(form.value?.operationsIds && form.value.operationsIds.length > 0){
            filters.operationsIds = form.value.operationsIds;
            filters.nbFiltersFilled++;
        }        
        if(form.value?.areasIds && form.value.areasIds.length > 0){
            filters.areasIds = form.value.areasIds;
            filters.nbFiltersFilled++;
        }
        if(form.value?.sectorsIds && form.value.sectorsIds.length > 0){
            filters.sectorsIds = form.value.sectorsIds;
            filters.nbFiltersFilled++;
        }
        if(form.value?.agentsIds && form.value.agentsIds.length > 0){
            filters.agentsIds = form.value.agentsIds;
            filters.nbFiltersFilled++;
        }
        if(form.value?.retailersCodes && form.value.retailersCodes.length > 0){
            filters.retailersCodes = form.value.retailersCodes;
            filters.retailersItems = retailersItems;
            filters.nbFiltersFilled++;
        }
        if(form.value?.storesCodes && form.value.storesCodes.length > 0){
            filters.storesCodes = form.value.storesCodes;
            filters.storesItems = storesItems;
            filters.nbFiltersFilled++;
        }
        if(form.value?.city && form.value.city != ''){
            filters.city = form.value.city;
            filters.nbFiltersFilled++;
        }
        if(form.value?.displayCancelledMissions === true){
            filters.displayCancelledMissions = true;
            filters.nbFiltersFilled++;
        }
        if(form.value?.displayOnlyStoreMissions === true){
            filters.displayOnlyStoreMissions = true;
            filters.nbFiltersFilled++;
        }
        if(form.value?.displayOnlyUnratedMissions === true){
            filters.displayOnlyUnratedMissions = true;
            filters.nbFiltersFilled++;
        }
        return filters;
    }
}

@Component({
    selector: 'app-planning-filters',
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss'],
    imports: [
        IonGrid, IonRow, IonCol, TranslatePipe, ReactiveFormsModule, 
        forwardRef(() => MarvInputComponent), forwardRef(() => MarvButtonComponent), forwardRef(() => MarvToggleComponent), forwardRef(() => MarvInputDateComponent), 
        forwardRef(() => MarvMonthSelectComponent), forwardRef(() => MarvWeekSelectComponent), forwardRef(() => MarvWeekDaySelectComponent),
        forwardRef(() => RetailerSelectComponent), forwardRef(() => AreaSelectComponent), forwardRef(() => SectorSelectComponent), 
        forwardRef(() => TypeMissionSelectComponent), forwardRef(() => OperationSelectComponent), forwardRef(() => StoreSelectComponent), 
        forwardRef(() => PermanentAgentSelectComponent) 
    ]
})
export class PlanningFiltersComponent  implements OnInit {

    @Input({alias : 'operations', required : true}) operations: Array<OperationCard> = [];
    @Input({alias : 'planningFiltersValues', required : false}) planningFiltersValues: PlanningFiltersValues = new PlanningFiltersValues();
    @Output() onChangePlanningFilters = new EventEmitter<PlanningFiltersValues>();
    
    @ViewChild('retailerSelect') retailerSelect : RetailerSelectComponent;
    @ViewChild('storeSelect') storeSelect : StoreSelectComponent;
    
    startDate: Date = this.planningFiltersValues.startDate;
    endDate: Date = this.planningFiltersValues.endDate;
    dateStartSubscription: Subscription;
    endDateSubscription: Subscription;
    monthSubscription: Subscription;
    weekStartSubscription: Subscription;
    updateAreasSubscription: Subscription;
    updateRetailersSubscription: Subscription;
    hasPermanentOperation: boolean = false;
    externalSourceRetailers: MarvSelectExternalSource|undefined;
    stores: Array<MarvSelectItem> = [];
    externalSourceStores: MarvSelectExternalSource|undefined;
    sectors: Array<MarvSelectItem> = [];
    hideSectors: boolean = false;

    filtersPlanningForm: FormGroup = new FormGroup({
        startDate: new FormControl(null),
        endDate: new FormControl(null),
        month: new FormControl(null),
        weekDays: new FormControl([]),
        week: new FormControl(null),
        typesMissionsCodes: new FormControl([]),
        operationsIds: new FormControl([]),
        areasIds: new FormControl([]),
        sectorsIds: new FormControl([]),
        agentsIds: new FormControl([]),
        retailersCodes: new FormControl([]),
        storesCodes: new FormControl([]),
        city: new FormControl(''),
        displayCancelledMissions: new FormControl(false),
        displayOnlyStoreMissions: new FormControl(false),
        displayOnlyUnratedMissions: new FormControl(false),
    });
    
    constructor(
        
    ) { }

    ngOnInit() {
        
        this.hasPermanentOperation = (OperationService.Operations.filter(o => o.type === 'lgg').length > 0);        
      
        this.dateStartSubscription = this.filtersPlanningForm.controls['startDate'].valueChanges.subscribe(newValue => {
            this.getPeriodDates();
        });
        this.endDateSubscription = this.filtersPlanningForm.controls['endDate'].valueChanges.subscribe(newValue => {
           this.getPeriodDates();
        });
        this.monthSubscription = this.filtersPlanningForm.controls['month'].valueChanges.subscribe(newValue => {
           this.getPeriodDates();
        });
        this.weekStartSubscription = this.filtersPlanningForm.controls['week'].valueChanges.subscribe(newValue => {
           this.getPeriodDates();
        });
        
        if(!RetailerService.Retailers || RetailerService.Retailers.length === 0){
            this.initExternalSourceRetailers();
        }
        
        if(StoreService.Stores && StoreService.Stores.length > 0){
            this.filterStores();
            this.updateRetailersSubscription = this.filtersPlanningForm.controls['retailersCodes'].valueChanges.subscribe(value => {
                this.filterStores();
            }); 
        }
        else{
            this.initExternalSourceStores();
        }
        
        this.filterSectors();        
        this.updateAreasSubscription = this.filtersPlanningForm.controls['areasIds'].valueChanges.subscribe(value => {
            this.filterSectors();
        }); 
    }
    
    ngOnDestroy() {
        if(this.dateStartSubscription){
            this.dateStartSubscription.unsubscribe();
        } 
        if(this.endDateSubscription){
            this.endDateSubscription.unsubscribe();
        } 
        if(this.monthSubscription){
            this.monthSubscription.unsubscribe();
        } 
        if(this.weekStartSubscription){
            this.weekStartSubscription.unsubscribe();
        } 
        if(this.updateRetailersSubscription){
            this.updateRetailersSubscription.unsubscribe();
        }
        if(this.updateAreasSubscription){
            this.updateAreasSubscription.unsubscribe();
        }
    }

    async ngOnChanges(changes: SimpleChanges) {
        if(changes['planningFiltersValues'] && changes['planningFiltersValues'].currentValue){           
            let values = {
                startDate: (this.planningFiltersValues.startDate ? this.planningFiltersValues.startDate : null), 
                endDate: (this.planningFiltersValues.endDate ? this.planningFiltersValues.endDate : null), 
                month: (this.planningFiltersValues.month ? this.planningFiltersValues.month : null), 
                weekDays: (this.planningFiltersValues.weekDays?.length > 0 ? this.planningFiltersValues.weekDays : []),
                week: (this.planningFiltersValues.week ? this.planningFiltersValues.week : null), 
                typesMissionsCodes: (this.planningFiltersValues.typesMissionsCodes?.length > 0 ? this.planningFiltersValues.typesMissionsCodes : []),
                operationsIds: (this.planningFiltersValues.operationsIds?.length > 0 ? this.planningFiltersValues.operationsIds : []),
                areasIds: (this.planningFiltersValues.areasIds?.length > 0 ? this.planningFiltersValues.areasIds : []),
                sectorsIds: (this.planningFiltersValues.sectorsIds?.length > 0 ? this.planningFiltersValues.sectorsIds : []),
                agentsIds: (this.planningFiltersValues.agentsIds?.length > 0 ? this.planningFiltersValues.agentsIds : []),
                retailersCodes: (this.planningFiltersValues.retailersCodes?.length > 0 ? this.planningFiltersValues.retailersCodes : []),
                storesCodes: (this.planningFiltersValues.storesCodes?.length > 0 ? this.planningFiltersValues.storesCodes : []),           
                city: (this.planningFiltersValues.city && this.planningFiltersValues.city != '' ? this.planningFiltersValues.city : ''),                
                displayCancelledMissions: (this.planningFiltersValues.displayCancelledMissions === true),               
                displayOnlyStoreMissions: (this.planningFiltersValues.displayOnlyStoreMissions === true),                     
                displayOnlyUnratedMissions: (this.planningFiltersValues.displayOnlyUnratedMissions === true),            
            };  
            this.filtersPlanningForm.setValue(values);            
        }
        
        if(changes['operations'] && changes['operations'].currentValue){     
            
            let operationsIds: Array<number> = [];
            let permanentsOperationsIds: Array<number> = [];
            
            this.operations.filter(o => o.type === 'loga').forEach(o => {
                operationsIds.push(o.id);
            });
            
            this.operations.filter(o => o.type === 'lgg').forEach(o => {
                permanentsOperationsIds.push(o.id);
            });
           
        }
    }
    
    getPeriodDates(){
        if(this.planningFiltersValues.viewSelected === 'month'){
            this.startDate = MarvDateService.GetFirstDayOfMonth(this.filtersPlanningForm.controls['month'].value);
            this.endDate = MarvDateService.GetLastDayOfMonth(this.filtersPlanningForm.controls['month'].value);
        }
        else if(this.planningFiltersValues.viewSelected === 'week'){
            this.startDate = MarvDateService.GetFirstDayOfWeek(this.filtersPlanningForm.controls['week'].value);
            this.endDate = MarvDateService.GetLastDayOfWeek(this.filtersPlanningForm.controls['week'].value);            
        }
        else{           
            this.startDate = this.filtersPlanningForm.controls['startDate'].value;          
            this.endDate = this.filtersPlanningForm.controls['endDate'].value;            
        }        
    }
    
    onChangeOperationsItems(operationsItems: Array<MarvSelectItem>){        
        let tabIds = this.filtersPlanningForm.controls['operationsIds'].value || [];
        let tabIdsItems = operationsItems.map(i => i.value);
        tabIds = tabIds.filter((i: number) => tabIdsItems.indexOf(i) !== -1);
        this.filtersPlanningForm.controls['operationsIds'].setValue(tabIds);
    }
    
    initExternalSourceRetailers(){
        let opIds = this.parseOperationsIds();
        this.externalSourceRetailers = {
            url: 'retailer',
            params : {
                opIDs: opIds.operationsIds,
                ctxIDs: opIds.contextsIds
            },
            labelField: 'name',
            valueField: 'code',
            transformItem: (json: any): MarvSelectItem => {                    
                return {
                    label: json['name'],
                    value: json['code'],
                    color: MarvColorPrimary,
                    picture: environment.URL_LOGO_RETAILER.replace('#retailer_name#', json['name'])
                };
            }
        };
    }
    
    initExternalSourceStores(){
        let opIds = this.parseOperationsIds();
        this.externalSourceStores = {
            url: 'store',
            params : {
                opIDs: opIds.operationsIds,
                ctxIDs: opIds.contextsIds
            },
            labelField: 'name',
            valueField: 'code',
            transformItem: (json: any): MarvSelectItem => {                    
                return {
                    label: json['name'],
                    value: json['code'],
                    color: MarvColorPrimary
                };
            }
        };
    }
    
    parseOperationsIds(){
        let operationsIds: Array<number> = [];          
        let contextsIds: Array<number> = [];          
        if(this.filtersPlanningForm.controls['operationsIds'].value?.length > 0){
            this.filtersPlanningForm.controls['operationsIds'].value.forEach((o: string) => {
                let opInfos = o.split('|');
                if(opInfos.length === 2){
                    if(opInfos[0] === 'loga'){
                        operationsIds.push(parseInt(opInfos[1]));
                    }
                    else if(opInfos[0] === 'lgg'){
                        contextsIds.push(parseInt(opInfos[1]));
                    }
                }
            });
        }
        else{
            OperationService.Operations.forEach(o => {
                if(o.type === 'loga'){
                    operationsIds.push(o.id);
                }
                else if  (o.type === 'lgg'){
                    contextsIds.push(o.id);
                }           
            });
        }
        return {operationsIds: operationsIds, contextsIds: contextsIds};
    }
    
    filterStores(){
        let stores: Array<MarvSelectItem> = [];
        let retailersCodes = this.filtersPlanningForm.controls['retailersCodes'].value;
        let storesCodes = this.filtersPlanningForm.controls['storesCodes'].value;
        for(let s of StoreService.Stores){
            if((!retailersCodes || retailersCodes.length === 0) || retailersCodes.indexOf(s.retailer.code) !== -1){
                stores.push(new MarvSelectItem(s.name, s.code, environment.URL_LOGO_RETAILER.replace('#retailer_name#', s.retailer.name)));
            }
            else{
                let indexSector = storesCodes.indexOf(s.code)
                if(indexSector !== -1){
                    storesCodes.splice(indexSector, 1);
                }
            }
        }   
        this.filtersPlanningForm.controls['storesCodes'].setValue(storesCodes, {emitEvent: false}); 
        this.stores = stores;
    }
    
    filterSectors(){        
        let sectors: Array<MarvSelectItem> = [];
        let areasIds = this.filtersPlanningForm.controls['areasIds'].value;
        let sectorsIds = this.filtersPlanningForm.controls['sectorsIds'].value;
        if(CustomerSectorService.Sectors && CustomerSectorService.Sectors.length > 0){
            this.hideSectors = false;
            for(let s of CustomerSectorService.Sectors){
                if((!areasIds || areasIds.length === 0) || areasIds.indexOf(s.area?.id) !== -1){
                    sectors.push(new MarvSelectItem(s.name, s.id));
                }
                else{
                    let indexSector = sectorsIds.indexOf(s.id)
                    if(indexSector !== -1){
                        sectorsIds.splice(indexSector, 1);
                    }
                }
            }   
        }
        else{
            this.hideSectors = true;
        }
        this.filtersPlanningForm.controls['sectorsIds'].setValue(sectorsIds, {emitEvent: false});     
        this.sectors = sectors;
    }
    
    search() {        
        if(this.filtersPlanningForm.valid){      
            let retailersItemsSelected = this.retailerSelect?.marvSelect?.getItemsSelected() || [];
            let storesItemsSelected = this.storeSelect?.marvSelect?.getItemsSelected() || [];
            let filtersValues = PlanningFiltersValues.FromForm(this.filtersPlanningForm, this.planningFiltersValues.viewSelected, retailersItemsSelected, storesItemsSelected);     
            filtersValues.saveFilters();       
            this.onChangePlanningFilters.emit(filtersValues);
        }
        else{
            for (var controlName in this.filtersPlanningForm.controls) {
                if(!this.filtersPlanningForm.controls[controlName].valid) {
                    this.filtersPlanningForm.controls[controlName].markAsTouched();
                }
            }
        }
    }
    
    displayAll() {
        let filters = new PlanningFiltersValues();
        filters.viewSelected = this.planningFiltersValues.viewSelected;
        filters.weekDays = [1,2,3,4,5,6,7];
        filters.saveFilters();            
        this.filtersPlanningForm.reset();
        this.onChangePlanningFilters.emit(filters);
    }
}
